// 事前チェックの各 step で realtime に渡す instructions と tools
// (spec 2026-08-29 §3.1 / D8、段階 2)。
//
// **読み上げる文言は cascade と同じ TTS_PHRASES から取る。** ここがずれると、
// 2 つのエンジンを比べたときに出る差が「エンジンの差」ではなく「文言の差」になる。
//
// 氏名の instructions は spike で 14/30 → 18/30 に効いた 2 節 (Entity Capture /
// Reference Pronunciations) を持つ。**思いつきで書き換えないこと** —
// n=30 での prompt 調整は過剰適合する (2026-08-18 に 1 行足して 67%→63% に落とした実測)。

import { TTS_PHRASES } from "../audio/ttsPhrases";

/**
 * 読み上げるだけの step (スピーカー確認 / チュートリアル / 完了案内)。
 * 相手の応答は待たず、読み終わったら narration_done を呼ばせる。
 */
export function buildNarrationInstructions(lines: string[]): string {
  const numbered = lines.map((l, i) => `${i + 1}. ${l}`).join("\n");
  return `あなたは日本語の音声アンケートの案内役です。必ず日本語だけで話してください。

# 読み上げる文
次の文を、書かれている順に、一字一句そのまま読み上げてください。
言い換え・要約・省略をしないでください。文を足さないでください。

${numbered}

# 手順
- 上の文を読み上げる。
- 読み終わったら narration_done を呼ぶ。
- 相手の応答を待たない。質問を返さない。相槌を打たない。`;
}

/**
 * 氏名の聞き取り。**復唱して確認してから確定する** (spec D8 の「最後のゲート」)。
 * 復唱そのものはモデルに任せる (2026-08-30 のユーザー判断) — realtime では声で
 * 答えるので、cascade で問題になった「読み終わる前のボタン早押し」は構造的に起きない。
 */
export function buildNameInstructions(): string {
  return `あなたは日本語の音声アンケートの事前確認を担当します。必ず日本語だけで話してください。

# 最初にすること
次の文を、一字一句そのまま読み上げてください。

${TTS_PHRASES.namePrompt}

# 固有名詞の扱い
- 氏名は**未知の固有名詞**である。知っている名前や有名人の名前に寄せて直さない。
  聞こえた音をそのまま写すこと。
- 音を 1 つも足さず、1 つも落とさない。とくに 3 文字以上の姓では中間の音を落としやすい。
- 語頭の子音 (カ行・タ行・ハ行・ナ行・マ行) は取り違えやすい。聞こえたとおりに書く。
- 「です」「といいます」「と申します」などの語尾は氏名に含めない。

# 読みの書き方
- 必ずカタカナで書く。ひらがな・漢字・ローマ字を混ぜない。
- 姓と名の間に空白・中黒を入れない。続けて 1 語で書く。
- 長音は「ー」、促音は「ッ」で書く。

# 手順
1. 相手が名乗るのを聞く。
2. 聞き取った氏名を**復唱して確認する**。「〜さん、ですね」のように、カタカナの読みで
   一度声に出すこと。復唱せずに確定してはいけない。
3. 肯定されたら confirm_name を呼ぶ。姓と名の両方が取れていれば full を true にする。
4. 違うと言われたら、もう一度お名前を伺い、2 に戻る。
5. 音がまったく判別できないときだけ need_repeat を呼ぶ。少しでも氏名らしく聞こえたなら、
   自信がなくても復唱して確認すること。`;
}

export const NAME_TOOLS: unknown[] = [
  {
    type: "function",
    name: "confirm_name",
    description: "本人が復唱を肯定したときに呼ぶ。確認していない氏名で呼んではいけない。",
    parameters: {
      type: "object",
      properties: {
        katakana: {
          type: "string",
          description: "氏名の読み。カタカナのみ。姓と名の間に空白を入れない。",
        },
        full: { type: "boolean", description: "姓と名の両方が取れたか" },
      },
      required: ["katakana"],
    },
  },
  {
    type: "function",
    name: "need_repeat",
    description: "氏名がまったく聞き取れないときに呼ぶ。",
    parameters: {
      type: "object",
      properties: { reason: { type: "string" } },
      required: ["reason"],
    },
  },
];

export const NARRATION_TOOLS: unknown[] = [
  {
    type: "function",
    name: "narration_done",
    description: "指定された文をすべて読み終えたときに呼ぶ。",
    parameters: { type: "object", properties: {}, required: [] },
  },
];

/** スピーカー確認で読ませる文 (cascade の SpeakerCheck と同じ)。 */
export const SPEAKER_CHECK_LINES = [TTS_PHRASES.speakerTest];

/** 遊び方チュートリアルの 3 ビート (cascade の Tutorial と同じ)。 */
export const TUTORIAL_LINES = [
  TTS_PHRASES.tutorialBeat1,
  TTS_PHRASES.tutorialBeat2,
  TTS_PHRASES.tutorialBeat3,
];

/** 完了案内 (cascade の ready step と同じ)。 */
export const READY_LINES = [TTS_PHRASES.ready];
